// src/lib/telemetry.ts
// Client-side telemetry: log page views, client errors, and slow requests
// to Analytics Engine. Also exposes a /api/track endpoint for the worker to
// record server-side issues (failed logins, etc.) for the operator.
//
// View: Cloudflare dashboard → Workers & Pages → sendr-prod → Analytics Engine → sendr-metrics

import { api } from "./api";

const SESSION_ID = (() => {
  let id = sessionStorage.getItem("sendr.sid");
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem("sendr.sid", id);
  }
  return id;
})();

function send(path: string, payload: Record<string, unknown>) {
  // Best-effort: don't block UI, never throw
  fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...payload, sid: SESSION_ID, ts: Date.now() }),
    keepalive: true,
  }).catch(() => undefined);
}

export const telemetry = {
  // Track a page view
  page(path: string, title?: string) {
    send("/api/track/page", { path, title: title || document.title });
  },

  // Track a client-side error (uncaught exceptions, React errors, etc.)
  error(err: Error | string, context?: Record<string, unknown>) {
    const e = err instanceof Error ? err : new Error(String(err));
    send("/api/track/error", {
      name: e.name,
      message: e.message,
      stack: (e.stack || "").slice(0, 2000),
      url: location.href,
      ...context,
    });
  },

  // Track a slow API request
  slowApi(path: string, durationMs: number) {
    send("/api/track/slow", { path, durationMs });
  },
};

// Install global handlers
let installed = false;
export function installTelemetry() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // Uncaught errors
  window.addEventListener("error", (e) => {
    telemetry.error(e.error || e.message, { kind: "window.onerror" });
  });

  // Unhandled promise rejections
  window.addEventListener("unhandledrejection", (e) => {
    telemetry.error(e.reason || "Unhandled rejection", { kind: "unhandledrejection" });
  });

  // Slow API detection (wraps the existing api client)
  const origGet = api.get.bind(api);
  api.get = async <T,>(p: string) => {
    const start = performance.now();
    try {
      return await origGet<T>(p);
    } finally {
      const ms = performance.now() - start;
      if (ms > 3000) telemetry.slowApi(p, ms);
    }
  };
}
